// @flow
import { Fragment, type Node, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import Icon from '@tablekit/icon';
import { Spring, animated } from 'react-spring/renderprops.cjs';
import LazyLoad from 'react-lazyload';
import {
  SPACING,
  COLORS,
  FONT_WEIGHTS,
  SIZES,
  typography
} from '@tablekit/theme';
import { Link as StyledLink } from '@tablekit/typography';
import StoryblokEditor from 'components/editable/StoryblokEditor';
import Button from '@tablekit/button';
import Link from 'components/GatsbyLink';
import StoryblokLink from 'components/Link';
import CallToAction from 'components/CallToAction';
import { LAZY_LOAD_OFFSET, MAX_WIDTH } from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import type { FooterType, LayoutType } from './types';
import { imageTransform } from './Image';

type PropsType = {
  config: FooterType,
  homeUrl: string,
  layout: LayoutType
};

const Container = styled.div`
  position: relative;
  max-width: ${MAX_WIDTH};
  margin: 0 auto;
  color: ${COLORS.GRAY.L1};
  padding: ${SPACING.L8} ${SPACING.L4} ${SPACING.L9};
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;

  @media (min-width: 768px) {
    padding: ${SPACING.L8} ${SPACING.L7} ${SPACING.L9};
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    bottom: 0;
    width: 102vw;
    transform: translateX(-50%);
    background: ${COLORS.GRAY.L9};
    z-index: 0;
  }

  > * {
    z-index: 1;
  }
`;

const MobileIcon = styled(Icon)`
  display: flex;

  @media (min-width: 768px) {
    display: none;
  }
`;

const StaticColumnHeader = styled.div`
  white-space: nowrap;
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-bottom: ${SPACING.L3};
  ${typography('L4')};

  @media (min-width: 768px) {
    ${typography('L5')}
  }
`;

const ColumnHeaderWrapper = styled(StaticColumnHeader)`
  cursor: pointer;
  display: flex;
  justify-content: space-between;

  padding-bottom: ${SPACING.L2};
  border-bottom: 1px solid white;

  @media (min-width: 768px) {
    border-bottom: none;
    padding-bottom: 0;
    cursor: auto;
  }
`;

const ColumnContent = animated(styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  align-items: flex-start;

  @media (min-width: 768px) {
    height: auto !important;
  }
`);

const LogoColumnContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 ${columnWidth(2)}%;
  width: 100%;
  margin: 0 0 ${SPACING.L2};

  & > ${ColumnHeaderWrapper} > ${MobileIcon} {
    transition: transform 0.3s ease-in-out;
    ${({ isSelected }) =>
      isSelected
        ? css`
            transform: rotate(180deg);
          `
        : css`
            transform: rotate(0deg);
          `}
  }
  @media (min-width: 768px) {
    width: auto;
    margin: 0 ${SPACING.L5} ${SPACING.L6};
  }
`;

const SocialButtonsColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin: 0 0 ${SPACING.L2};

  @media (min-width: 768px) {
    margin: 0 ${SPACING.L5} ${SPACING.L6};
  }
`;

const ActionButtonsColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  flex: 1 1 auto;
  padding: ${SPACING.L8} 0;
  margin: 0;

  @media (min-width: 768px) {
    justify-content: flex-start;
    margin: 0 ${SPACING.L5};
    padding: 0;
  }
`;

const ActionButton = styled(Button)`
  &:visited {
    color: white;
  }
  &:not(:last-of-type) {
    margin-bottom: ${SPACING.L6};
  }
  @media (min-width: 768px) {
    margin-bottom: ${SPACING.L6};
  }
  justify-content: center;
`;

const MobileActionButton = styled(ActionButton)`
  display: inline;
  @media (min-width: 768px) {
    display: none;
  }
`;

const NonMobileActionButton = styled(ActionButton)`
  display: none;
  @media (min-width: 768px) {
    display: inline;
  }
`;

const ColumnHeader = ({ children, ...props }: *) => (
  <ColumnHeaderWrapper {...props}>
    <div>{children}</div>
    <MobileIcon icon={faChevronDown} />
  </ColumnHeaderWrapper>
);

const ColumnItem = styled(StyledLink)`
  text-align: left;
  outline: none;
  cursor: pointer;
  white-space: nowrap;

  color: ${COLORS.GRAY.L1};
  &:visited {
    color: ${COLORS.GRAY.L3};
  }
  &:hover,
  &:active {
    color: ${COLORS.GRAY.L4};
  }

  ${typography('L3')};
  padding: ${SPACING.L2} 0;
  margin-bottom: 0;
  @media (min-width: 768px) {
    ${typography('L4')};
    padding: 0;
    margin-bottom: ${SPACING.L2};
  }
`;

const MobileColumnItem = styled(ColumnItem)`
  display: inline;
  @media (min-width: 768px) {
    display: none;
  }
`;
const NonMobileColumnItem = styled(ColumnItem)`
  display: none;
  @media (min-width: 768px) {
    display: inline;
  }
`;

const socialMediaIconSize = 32;

const SocialMediaRow = styled.div`
  display: flex;
  flex-direction: row;

  > a:not(:last-of-type) {
    margin-right: ${SPACING.L4};
    @media (min-width: 768px) {
      margin-right: ${SPACING.L3};
    }
  }
`;

const ContentRow = styled.div`
  display: flex;
  max-width: ${MAX_WIDTH};
  width: 100%;
  margin: 0 auto;
  align-items: stretch;
  flex-direction: column;
  flex: 1 1 auto;

  @media (min-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
  }
`;
const FooterRow = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: stretch;
  flex-direction: column-reverse;
  max-width: ${MAX_WIDTH};
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    border-top: 1px solid white;
    & > * {
      margin-top: ${SPACING.L6};
    }
  }
`;
const ContentColumns = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  margin: 0;

  @media (min-width: 768px) {
    margin: 0 -${SPACING.L5};
    align-items: flex-start;
  }
`;
const LeftContentColumns = styled(ContentColumns)`
  flex: 1 1 100%;
  flex-direction: column;

  @media (min-width: 768px) {
    padding-right: ${SPACING.L5};
  }

  @media (min-width: 1020px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;
const RightContentColumns = styled(ContentColumns)`
  flex: 1 1 auto;
  justify-content: flex-start;
  flex-direction: column;
`;
const PolicyColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1 1 auto;
`;
const PolicyRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  flex-direction: column;

  @media (min-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    &:not(:last-of-type) {
      margin-bottom: ${SPACING.L4};
    }
  }
`;
const RowLink = styled(ColumnItem.withComponent(StoryblokLink))`
  margin-left: 0;
  margin-bottom: 0;
  @media (min-width: 768px) {
    margin-left: ${SPACING.L6};
  }
`;

const imageHover = css`
  &:hover {
    opacity: 0.8;
  }
`;

const SocialMediaImage = styled.img`
  height: ${socialMediaIconSize}px;
  width: ${socialMediaIconSize}px;
  ${imageHover};
`;

const LogoImage = styled.img`
  display: none;
  @media (min-width: 768px) {
    display: flex;
  }
  height: 32px;
  ${imageHover};
`;

const LogoSubtext = styled.div`
  ${typography('L4')};
  margin-top: ${SPACING.L6};
  @media (min-width: 768px) {
    margin-top: ${SPACING.L2};
  }
`;

const closedColumnStyle = {
  height: 0
};
const openColumnStyle = {
  height: 'auto'
};

const getColumnSpringProps = (isOpen) => ({
  native: true,
  from: closedColumnStyle,
  to: isOpen ? openColumnStyle : closedColumnStyle
});

export default function Footer({
  homeUrl,
  config: {
    action_buttons: actionButtons = [],
    footer_logo: [logo],
    footer_logo_link: logoLink,
    navigation_items: navigationLists,
    policy_link_rows: policyRows,
    social_links: [socialLinks]
  },
  config: footer,
  layout: {
    call_to_action: [callToAction]
  }
}: PropsType): Node {
  const [selectedSubnav, setSelectedSubnav] = useState(null);
  const toggleSelectedSubnav = (clickedNav) => {
    setSelectedSubnav(selectedSubnav === clickedNav ? null : clickedNav);
  };
  const content = (
    <Container>
      <ContentRow>
        <LeftContentColumns>
          {navigationLists.slice(0, 5).map(({ _uid: id, items, title }) => (
            <Column key={id} isSelected={selectedSubnav === id}>
              <ColumnHeader onClick={() => toggleSelectedSubnav(id)}>
                {title}
              </ColumnHeader>
              <Spring {...getColumnSpringProps(selectedSubnav === id)}>
                {(style) => (
                  <ColumnContent style={style}>
                    {items.map(
                      ({ _uid, label, is_app_link: isAppLink, value }) => {
                        if (isAppLink) {
                          return (
                            <Fragment key={_uid}>
                              <MobileColumnItem href={CONFIG.mobileAppLoginUrl}>
                                {label}
                              </MobileColumnItem>
                              <NonMobileColumnItem href={CONFIG.appLoginUrl}>
                                {label}
                              </NonMobileColumnItem>
                            </Fragment>
                          );
                        }
                        return (
                          <ColumnItem
                            key={_uid}
                            link={value}
                            as={StoryblokLink}
                          >
                            {label}
                          </ColumnItem>
                        );
                      }
                    )}
                  </ColumnContent>
                )}
              </Spring>
            </Column>
          ))}
        </LeftContentColumns>
        <RightContentColumns>
          {actionButtons.length > 0 && (
            <ActionButtonsColumn>
              {actionButtons.map(
                ({ _uid: key, label, value, is_app_link: isAppLink }) => {
                  if (isAppLink) {
                    return (
                      <Fragment key={key}>
                        <MobileActionButton
                          iconAfter={<Icon icon={faChevronRight} />}
                          appearance="primary"
                          href={CONFIG.mobileAppLoginUrl}
                        >
                          {label}
                        </MobileActionButton>
                        <NonMobileActionButton
                          iconAfter={<Icon icon={faChevronRight} />}
                          appearance="primary"
                          size={SIZES.LARGE}
                          href={CONFIG.appLoginUrl}
                        >
                          {label}
                        </NonMobileActionButton>
                      </Fragment>
                    );
                  }
                  return (
                    <ActionButton
                      key={key}
                      appearance="primary"
                      iconAfter={<Icon icon={faChevronRight} />}
                      size={{
                        default: SIZES.REGULAR,
                        '(min-width: 768px)': SIZES.LARGE
                      }}
                      component={StoryblokLink}
                      link={value}
                    >
                      {label}
                    </ActionButton>
                  );
                }
              )}
            </ActionButtonsColumn>
          )}
          <SocialButtonsColumn>
            <StaticColumnHeader>{socialLinks.title}</StaticColumnHeader>
            <SocialMediaRow>
              {socialLinks.items.map(({ _uid, alt, image, link: { url } }) => (
                <StoryblokLink key={_uid} href={url}>
                  <LazyLoad offset={LAZY_LOAD_OFFSET}>
                    <SocialMediaImage alt={alt} src={imageTransform(image)} />
                  </LazyLoad>
                </StoryblokLink>
              ))}
            </SocialMediaRow>
          </SocialButtonsColumn>
        </RightContentColumns>
      </ContentRow>
      <FooterRow>
        <StoryblokEditor content={logo}>
          <LogoColumnContent>
            {logoLink ? (
              <StoryblokLink link={logoLink}>
                <LazyLoad offset={LAZY_LOAD_OFFSET}>
                  <LogoImage src={imageTransform(logo.white)} alt={logo.alt} />
                </LazyLoad>
              </StoryblokLink>
            ) : (
              <Link to={homeUrl}>
                <LazyLoad offset={LAZY_LOAD_OFFSET}>
                  <LogoImage src={imageTransform(logo.white)} alt={logo.alt} />
                </LazyLoad>
              </Link>
            )}
            <LogoSubtext>{logo.subtext}</LogoSubtext>
          </LogoColumnContent>
          <PolicyColumn>
            {(policyRows || []).map(({ _uid: id, items }) => (
              <PolicyRow key={id}>
                {items.map(({ _uid, label, value }) => (
                  <RowLink key={_uid} link={value}>
                    {label}
                  </RowLink>
                ))}
              </PolicyRow>
            ))}
          </PolicyColumn>
        </StoryblokEditor>
      </FooterRow>
    </Container>
  );
  return (
    <Fragment>
      <CallToAction blok={callToAction} />
      <StoryblokEditor content={footer}>{content}</StoryblokEditor>
    </Fragment>
  );
}
